<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useCallStore } from '@/stores/useCallStore'
import { useUserStore } from '@/stores/useUserStore'
import { storeToRefs } from 'pinia'
import TimeLine from '@/components/common/ui/TimeLine.vue'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useEventStore } from '@/stores/useEventStore'
import { useMessageStore } from '@/stores/useMessageStore'

interface FormattedItem {
  id: number
  date: Date
  formattedDate: string
  user: { id?: number; name?: string }
  comment: string
  type: string
  doNotDisturb: boolean
  isOwner: boolean
}

const props = defineProps({
  partnerId: {
    type: Number,
    required: true,
  },
})

const callStore = useCallStore()
const { calls } = storeToRefs(callStore)

const userStore = useUserStore()
const boostCoreStore = useBoostCorePermissionStore()
const { userConnected } = storeToRefs(userStore)

const eventStore = useEventStore()
const { eventsPartner } = storeToRefs(eventStore)

const messageStore = useMessageStore()
const { messages } = storeToRefs(messageStore)

const formattedData = ref<FormattedItem[]>([])

const isLoadingData = ref(true)

const formatCallsAndEvents = () => {
  const formattedCalls = calls.value.map((call) => ({
    id: call.id,
    date: new Date(call.callDate),
    formattedDate: new Date(call.callDate).toLocaleString('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'medium',
    }),
    user: { id: call.userId.id, name: call.userId.name },
    comment: call.comment || '',
    type: 'call',
    doNotDisturb: call.doNotDisturb,
    isOwner: call.userId.id === userConnected.value?.id,
  }))

  const formattedEvents = eventsPartner.value.map((event) => {
    const userInfo = userStore.getLocalInfoUserById(event.userId)
    return {
      id: event.id,
      date: new Date(event.start),
      formattedDate: new Date(event.start).toLocaleString('fr-FR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }),
      user: { id: event.userId, name: userInfo?.name || 'Inconnu' },
      comment: event.name,
      type: 'event',
      doNotDisturb: false,
      isOwner: event.userId === userConnected.value?.id,
    }
  })

  const formattedMessages = messages.value.map((message) => ({
    id: message.id,
    date: new Date(message.date),
    formattedDate: new Date(message.date).toLocaleString('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }),
    user: { id: message.authorId.id, name: message.authorId.name },
    comment: message.subject
      ? `<strong>${message.subject}</strong><br>${message.body}`
      : message.body,
    type: 'message',
    doNotDisturb: false,
    isOwner: message.authorId.id === userConnected.value?.partnerId,
  })).filter((item) => item.isOwner)

  const formattedData = [...formattedEvents, ...formattedCalls, ...formattedMessages]

  return formattedData.sort((a, b) => b.date.getTime() - a.date.getTime())
}

onMounted(async () => {
  await Promise.all([
    callStore.getCallsByPartnerId(props.partnerId),
    eventStore.getEventsByPartnerId(props.partnerId),
    messageStore.getMessagesByPartnerId(props.partnerId),
  ])

  if (!userConnected.value) {
    await userStore.getUserByEmail(boostCoreStore.user?.email)
  }

  formattedData.value = formatCallsAndEvents()
  isLoadingData.value = false
})

watch(
  [calls, eventsPartner, messages],
  () => {
    formattedData.value = formatCallsAndEvents()
  },
  { deep: true },
)

</script>

<template>
  <div v-if="isLoadingData" class="skeleton opacity-30 m-4 h-[330px]"></div>
  <TimeLine v-if="!isLoadingData && formattedData.length !== 0" :items="formattedData" />
  <div v-if="!isLoadingData && formattedData.length === 0" class="text-center">
    Aucun RDV, appel ou To Do trouvé.
  </div>
</template>
